/**
 * 用戶資料管理服務
 * 負責 users 表的 CRUD 操作和快取管理
 */

import { supabase } from '@shared/services/core/supabaseClient'
import type { User as SupabaseUser } from '@supabase/supabase-js'
import type { AuthUser, UserProfile, UserRow } from '../auth/types'

// ============ 常數 ============

const USER_CACHE_DURATION = 5 * 60 * 1000 // 5 分鐘

// ============ 型別 ============

interface CacheEntry<T = unknown> {
  data: T
  timestamp: number
}

interface UserCache {
  [key: string]: CacheEntry<UserProfile> | undefined
}

// ============ 轉換函數 ============

export function convertUserRowToProfile(userData: UserRow | null): UserProfile {
  if (!userData) return {}

  return {
    id: userData.id,
    uid: userData.id,
    email: userData.email,
    displayName: userData.display_name,
    fullName: userData.full_name,
    photoURL: userData.photo_url,
    phoneNumber: userData.phone_number,
    title: userData.title,
    department: userData.department,
    address: userData.address,
    googleId: userData.google_id,
    facebookId: userData.facebook_id,
    lineId: userData.line_id,
    createdAt: userData.created_at,
    updatedAt: userData.updated_at,
    lastSelectedOrgId: userData.last_selected_org_id,
    lastOrgUpdateAt: userData.last_org_update_at,
    supabaseUserId: userData.supabase_user_id
  }
}

export function convertProfileToUserRow(profile: UserProfile): Partial<UserRow> {
  const supabaseData: Partial<UserRow> = {}

  if (profile.email !== undefined) supabaseData.email = profile.email
  if (profile.displayName !== undefined) supabaseData.display_name = profile.displayName
  if (profile.fullName !== undefined) supabaseData.full_name = profile.fullName
  if (profile.photoURL !== undefined) supabaseData.photo_url = profile.photoURL
  if (profile.phoneNumber !== undefined) supabaseData.phone_number = profile.phoneNumber
  if (profile.title !== undefined) supabaseData.title = profile.title
  if (profile.department !== undefined) supabaseData.department = profile.department
  if (profile.address !== undefined) supabaseData.address = profile.address
  if (profile.googleId !== undefined) supabaseData.google_id = profile.googleId
  if (profile.facebookId !== undefined) supabaseData.facebook_id = profile.facebookId
  if (profile.lineId !== undefined) supabaseData.line_id = profile.lineId
  if (profile.createdAt !== undefined) supabaseData.created_at = profile.createdAt
  if (profile.updatedAt !== undefined) supabaseData.updated_at = profile.updatedAt
  if (profile.lastSelectedOrgId !== undefined) supabaseData.last_selected_org_id = profile.lastSelectedOrgId
  if (profile.lastOrgUpdateAt !== undefined) supabaseData.last_org_update_at = profile.lastOrgUpdateAt
  if (profile.supabaseUserId !== undefined) supabaseData.supabase_user_id = profile.supabaseUserId

  return supabaseData
}

function cleanProviderName(providerName: string): string | null {
  if (providerName === 'google.com' || providerName === 'google') return 'google'
  if (providerName === 'facebook.com' || providerName === 'facebook') return 'facebook'
  if (providerName === 'oidc.line' || providerName === 'line') return 'line'
  return null
}

// ============ 服務類別 ============

class UserProfileService {
  private userCache: UserCache = {}

  private isUserCacheValid(cacheEntry: CacheEntry<UserProfile> | undefined): boolean {
    return !!cacheEntry && Date.now() - cacheEntry.timestamp < USER_CACHE_DURATION
  }

  clearUserCache(): void {
    this.userCache = {}
  }

  async createOrUpdateUserProfile(user: SupabaseUser | AuthUser): Promise<string> {
    const userId = (user as SupabaseUser)?.id || (user as AuthUser)?.uid
    const email = user?.email
    const displayName =
      (user as AuthUser)?.displayName ||
      (user as SupabaseUser)?.user_metadata?.display_name ||
      (user as SupabaseUser)?.user_metadata?.full_name ||
      (user as SupabaseUser)?.user_metadata?.name
    const photoURL =
      (user as AuthUser)?.photoURL ||
      (user as SupabaseUser)?.user_metadata?.photo_url ||
      (user as SupabaseUser)?.user_metadata?.avatar_url ||
      (user as SupabaseUser)?.user_metadata?.picture
    const phoneNumber = (user as SupabaseUser)?.phone || undefined

    const safeDisplayName = displayName ? String(displayName).slice(0, 255) : undefined
    const safePhotoURL = photoURL ? String(photoURL).slice(0, 2048) : undefined
    const safeEmail = email ? String(email).slice(0, 320) : undefined

    let providerName = 'email'
    let providerId: string | null = null

    const appProvider = (user as SupabaseUser)?.app_metadata?.provider
    const userProvider = (user as SupabaseUser)?.user_metadata?.provider
    if (appProvider) {
      const cleaned = cleanProviderName(appProvider)
      if (cleaned) { providerName = cleaned; providerId = userId }
    } else if (userProvider) {
      const cleaned = cleanProviderName(userProvider)
      if (cleaned) { providerName = cleaned; providerId = userId }
    }

    let userProfile: UserProfile | null = null
    if (email) {
      userProfile = await this.findUserByField('email', email)
    }
    if (!userProfile && userId) {
      userProfile = await this.getUserProfile(userId)
      if (!userProfile.id) userProfile = null
    }

    const now = new Date().toISOString()
    const providerIdField = (providerId && providerName) ? { [`${providerName}_id`]: providerId } : {}
    const supabaseUserId = (user as SupabaseUser)?.id || userId

    if (userProfile && userProfile.id) {
      const updateData: Record<string, unknown> = {
        email: safeEmail || userProfile.email,
        display_name: safeDisplayName || userProfile.displayName,
        full_name: safeDisplayName || userProfile.fullName || userProfile.displayName,
        photo_url: safePhotoURL || userProfile.photoURL,
        phone_number: phoneNumber || userProfile.phoneNumber,
        supabase_user_id: supabaseUserId,
        updated_at: now,
        ...providerIdField
      }

      const { error } = await supabase.from('users').update(updateData).eq('id', userProfile.id)
      if (error) throw error

      delete this.userCache[`user_${userProfile.id}`]
      return userProfile.id
    } else {
      const userData: Record<string, unknown> = {
        id: userId,
        email: safeEmail || '',
        display_name: safeDisplayName || '',
        full_name: safeDisplayName || '',
        photo_url: safePhotoURL || '',
        phone_number: phoneNumber || null,
        supabase_user_id: supabaseUserId,
        created_at: now,
        updated_at: now,
        ...providerIdField
      }

      const { error } = await supabase.from('users').insert(userData)
      if (error) throw error

      this.invalidateUser(userId)
      return userId
    }
  }

  async findUserByField(fieldName: string, fieldValue: string): Promise<UserProfile | null> {
    if (!fieldValue) return null

    const fieldMap: Record<string, string> = {
      email: 'email',
      googleId: 'google_id',
      facebookId: 'facebook_id',
      lineId: 'line_id',
      displayName: 'display_name',
      photoURL: 'photo_url',
      fullName: 'full_name',
      phoneNumber: 'phone_number'
    }

    const supabaseFieldName = fieldMap[fieldName]
    if (!supabaseFieldName) {
      console.error(`[UserProfileService] 不支援的查詢欄位: ${fieldName}`)
      return null
    }

    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq(supabaseFieldName, fieldValue)
      .limit(1)
      .maybeSingle()

    if (error && error.code !== 'PGRST116') {
      console.error('[UserProfileService] 查詢用戶失敗:', error)
      return null
    }

    if (data) {
      return convertUserRowToProfile(data as UserRow)
    }

    return null
  }

  async getUserProfile(user: string | AuthUser): Promise<UserProfile> {
    const userId = (typeof user === 'string' ? user : user?.uid || user?.id || '').trim()
    if (!userId) return {}

    const cacheKey = `user_${userId}`
    if (this.isUserCacheValid(this.userCache[cacheKey])) {
      return this.userCache[cacheKey]!.data
    }

    let { data: userData, error } = await supabase
      .from('users')
      .select('*')
      .eq('id', userId)
      .maybeSingle()

    if (!userData && !error) {
      // id 找不到，嘗試用 supabase_user_id 查詢
      const result = await supabase.from('users').select('*').eq('supabase_user_id', userId).maybeSingle()
      userData = result.data
      error = result.error
    }

    if (error) {
      console.error('[UserProfileService] 獲取用戶資料失敗:', error)
      return {}
    }

    if (userData) {
      const convertedData = convertUserRowToProfile(userData as UserRow)
      this.userCache[cacheKey] = { data: convertedData, timestamp: Date.now() }
      return convertedData
    }

    return {}
  }

  async updateUserProfile(userProfile: UserProfile): Promise<void> {
    const { id, ...restData } = userProfile
    if (!id) {
      throw new Error('更新用戶檔案需要提供用戶 ID')
    }

    const supabaseData = { ...convertProfileToUserRow(restData), updated_at: new Date().toISOString() }

    const { error } = await supabase.from('users').upsert(
      { id, ...supabaseData, created_at: supabaseData.created_at || new Date().toISOString() },
      { onConflict: 'id' }
    )

    if (error) throw error

    this.invalidateUser(id)
  }

  /** 清除指定用戶的快取 */
  invalidateUser(userId: string): void {
    delete this.userCache[`user_${userId}`]
  }
}

export const userProfileService = new UserProfileService()
export default userProfileService
